import { sanitize } from '@brandfolder/utilities';
import classnames from 'classnames';
import { highlight, languages } from 'prismjs/components/prism-core';
import React, { useState, useEffect } from 'react';
import Editor from 'react-simple-code-editor';
import styled from 'styled-components';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-markup';
import { CodeEditorLanguages } from '../../../../enums/code-editor.js';
import '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import { useMounted } from '../../../../hooks/use-mounted.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';

const UnstyledBaseCodeEditor = (props)=>{
    const { className, defaultValue, id, ignoreTabKey = false, insertSpaces = true, language = CodeEditorLanguages.Css, onBlur, onChange, onEdit, padding = 15, sanitizeConfig, tabSize = 2, ...otherProps } = props;
    const [edit, setEdit] = useState({
        isSanitized: false,
        value: defaultValue || ''
    });
    const isMounted = useMounted();
    useEffect(()=>{
        if (isMounted && onEdit) {
            onEdit(edit.value, edit.isSanitized);
        }
    }, [
        edit
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    return(// eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    /*#__PURE__*/ React.createElement(Editor, {
        ...otherProps,
        className: classnames({
            'bf-code-editor': true,
            [`${className}`]: !!className
        }),
        highlight: (code)=>highlight(code, languages[language]),
        ignoreTabKey: ignoreTabKey,
        insertSpaces: insertSpaces,
        onBlur: (event)=>{
            const e = event;
            const sanitized = sanitize(e.target.value, sanitizeConfig);
            setEdit({
                isSanitized: e.target.value !== sanitized,
                value: sanitized
            });
            if (onBlur) onBlur(e);
        },
        onValueChange: (code)=>{
            setEdit({
                isSanitized: false,
                value: code
            });
            if (onChange) onChange(code);
        },
        padding: padding,
        tabSize: tabSize,
        textareaId: id,
        value: edit.value
    }));
};
// prismjs/themes/prism.css
const StyledCodeEditor = styled(UnstyledBaseCodeEditor)`
  code[class*='language-'],
  pre[class*='language-'] {
    color: black;
    background: none;
    text-shadow: 0 1px white;
    font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
    font-size: 1em;
    text-align: left;
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    word-wrap: normal;
    line-height: 1.5;

    -moz-tab-size: 4;
    -o-tab-size: 4;
    tab-size: 4;

    -webkit-hyphens: none;
    -moz-hyphens: none;
    -ms-hyphens: none;
    hyphens: none;
  }

  pre[class*='language-']::-moz-selection,
  pre[class*='language-'] ::-moz-selection,
  code[class*='language-']::-moz-selection,
  code[class*='language-'] ::-moz-selection {
    text-shadow: none;
    background: #b3d4fc;
  }

  pre[class*='language-']::selection,
  pre[class*='language-'] ::selection,
  code[class*='language-']::selection,
  code[class*='language-'] ::selection {
    text-shadow: none;
    background: #b3d4fc;
  }

  @media print {
    code[class*='language-'],
    pre[class*='language-'] {
      text-shadow: none;
    }
  }

  /* Code blocks */
  pre[class*='language-'] {
    padding: 1em;
    margin: 0.5em 0;
    overflow: auto;
  }

  :not(pre) > code[class*='language-'],
  pre[class*='language-'] {
    background: #f5f2f0;
  }

  /* Inline code */
  :not(pre) > code[class*='language-'] {
    padding: 0.1em;
    border-radius: 0.3em;
    white-space: normal;
  }

  .token.comment,
  .token.prolog,
  .token.doctype,
  .token.cdata {
    color: slategray;
  }

  .token.punctuation {
    color: #999;
  }

  .token.namespace {
    opacity: 0.7;
  }

  .token.property,
  .token.tag,
  .token.boolean,
  .token.number,
  .token.constant,
  .token.symbol,
  .token.deleted {
    color: #905;
  }

  .token.selector,
  .token.attr-name,
  .token.string,
  .token.char,
  .token.builtin,
  .token.inserted {
    color: #690;
  }

  .token.operator,
  .token.entity,
  .token.url,
  .language-css .token.string,
  .style .token.string {
    color: #9a6e3a;
    background: hsla(0, 0%, 100%, 0.5);
  }

  .token.atrule,
  .token.attr-value,
  .token.keyword {
    color: #07a;
  }

  .token.function,
  .token.class-name {
    color: #dd4a68;
  }

  .token.regex,
  .token.important,
  .token.variable {
    color: #e90;
  }

  .token.important,
  .token.bold {
    font-weight: bold;
  }
  .token.italic {
    font-style: italic;
  }

  .token.entity {
    cursor: help;
  }
`;
const BaseCodeEditor = (props)=>{
    return /*#__PURE__*/ React.createElement(StyledCodeEditor, props);
};

export { BaseCodeEditor };
//# sourceMappingURL=base-code-editor.js.map
